import { addDays, addMonths } from "date-fns";

import { useUIStore } from "@/stores/uiStore";

/**
 *
 * @param date
 * @param freeze It is recommended to set it to `true`.
 * @returns
 */
export function convertToAPIDate(
  date: string | Date,
  freeze?: boolean,
): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());

    return freeze
      ? `${year}-${month}-${day}`
      : `${utcYear}-${utcMonth}-${utcDay}`;
  }
  return "";
}
/**
 *
 * @param date
 * @param freeze It is recommended to set it to `false`.
 * @returns
 */
export function convertToFullAPIDate(
  date: string | Date,
  freeze?: boolean,
): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());
    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());

    return freeze
      ? `${year}-${month}-${day} ${hour}:${minute}`
      : `${utcYear}-${utcMonth}-${utcDay} ${utcHour}:${utcMinute}`;
  }
  return "";
}

export function convertToTime(date: string | Date, freeze?: boolean): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());

    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());

    return freeze ? `${hour}:${minute}` : `${utcHour}:${utcMinute}`;
  }
  return "";
}

export function convertToTwoDigits(num: number | undefined): string {
  if (!num) return "00";
  if (num.toString()?.length >= 3) {
    return num?.toString();
  }
  return `00${num}`.slice(-2);
}

export const validateDate = (date: string | Date) => {
  if (!date) return false;
  const newDate = new Date(date);

  return !Number.isNaN(newDate.getTime());
};

export function useConvertDate() {
  const locale = useUIStore((state) => state.locale) === "fa" ? "fa" : "ar";

  function humanizeDate(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour12: false,
      });
    }
    return "- - -";
  }

  function humanizeDateWithWeekday(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        year: "numeric",
        month: "long",
        day: "2-digit",
        weekday: "long",
        hour12: false,
      });
    }
    return "- - -";
  }

  function humanizeTime(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return "- - -";
  }

  function humanizeFullTime(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    }
    return "- - -";
  }

  function humanizeFullDate(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return "- - -";
  }

  function humanizeFullDateWithWeekday(date: string | Date): string {
    if (validateDate(date)) {
      return new Date(date).toLocaleString(locale, {
        year: "numeric",
        month: "long",
        day: "2-digit",
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return "- - -";
  }

  return {
    humanizeDate,
    humanizeTime,
    humanizeFullTime,
    humanizeFullDate,
    humanizeFullDateWithWeekday,
    humanizeDateWithWeekday,
  };
}

export const getTomorrow = (days = 1) => addDays(new Date(), days);
export const getNextMonth = (nextMonth = 1) => addMonths(new Date(), nextMonth);

export const getTomorrowStart = () => {
  const date = addDays(new Date(), 1);

  date.setHours(0, 0, 0, 0);
  return date;
};
export function combineDates(date1: Date, date2: Date): Date {
  // Extract year, month, and day from date1
  const year = date1.getFullYear();
  const month = date1.getMonth();
  const day = date1.getDate();

  // Extract hours, minutes, seconds, and milliseconds from date2
  const hours = date2.getHours();
  const minutes = date2.getMinutes();

  // Create a new Date by combining the extracted parts
  return new Date(year, month, day, hours, minutes);
}
/**
 *
 * @param value it is used in fast date picker. when the data is in Y-M-D mode convert it to new Date() then the date will show in shamsi mode
 * @returns
 */
export function convertToDate(value: Date | string) {
  if (!!value && validateDate(value)) {
    return new Date(value);
  }
}
/**
 * if your date and time are separate and want to merge them as a unit date time,
 *  use this function
 * @param date date or string
 * @param time date or string
 * @returns date time
 */
export function mergeDateAndTime(date: string | Date, time: string | Date) {
  const mergedDate = new Date(date);
  const timeDate = new Date(time);

  mergedDate.setHours(timeDate.getHours(), timeDate.getMinutes());

  return mergedDate;
}

export function isPastDateTime(dateTime: Date | string) {
  return Date.now() > new Date(dateTime).getTime();
}

export const getDefaultTimezone = () => {
  if (import.meta.env.VITE_LANGUAGE === "fa") {
    return "Asia/Tehran";
  }

  return "Asia/Baghdad";
};
